<script setup lang="ts">
/**
 * InfiniteLoadingTrigger
 *
 * A reusable component that triggers loading of more content when it enters the viewport.
 * Uses the Intersection Observer API to detect visibility and emit a load event.
 *
 * @example
 * ```vue
 * <InfiniteLoadingTrigger
 *   :disabled="!hasMoreItems"
 *   @load="loadMoreItems"
 * >
 *   <div v-if="isLoading">Loading...</div>
 * </InfiniteLoadingTrigger>
 * ```
 *
 * @emits load - Emitted when the trigger enters the viewport
 * @prop {boolean} disabled - When true, prevents the load event from being emitted
 * @prop {number} threshold - Value between 0 and 1 indicating how much of the element
 *                           needs to be visible before triggering (default: 0.5)
 */

import CircularProgress from '@/uiKit/CircularProgress.vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useTemplateRef } from 'vue'

const props = defineProps<{
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'trigger'): void
}>()

const triggerRef = useTemplateRef<HTMLElement>('trigger')

useInfiniteScroll(
  triggerRef,
  () => {
    if (props.loading) return
    emit('trigger')
  },
  { distance: 50 },
)
</script>

<template>
  <div
    ref="trigger"
    class="flex w-full items-center justify-center gap-2"
  >
    <slot name="default">
      <CircularProgress v-if="loading" />
      <span class="text-sm-12px-light text-text-subtlest">
        <slot name="message">Loading more items...</slot>
      </span>
    </slot>
  </div>
</template>
